<template>
  <div class="kpt-search-component">
    <b-autocomplete 
      v-model="term"
      :data="data"
      field="term"
      placeholder="Search the Access Portal..."
      :open-on-focus="true"
      expanded
      :loading="isSearching"
      icon-pack="far"
      icon="search"
      @input="getSearchResults"
      ref="searchInput"
      spellcheck="false"
      >
      <template slot="header">
        <div class="mb-2" v-if="term.length <= 2">
          <b-field label="Search History" title="Queries typed by the user" style="margin-top: 0.5rem">
            <ul v-if="searchHistory.length">
              <li v-for="(val, idx) in searchHistory" :key="idx" @click="term = val.term">{{val.term}}</li>
            </ul>
            <div v-else>
              <p>No history yet.</p>
            </div>
          </b-field>
          <hr>
          <b-field label="Recent Results" title="Results selected by the user">
            <ul v-if="recentResults.length">
              <li v-for="(val, idx) in recentResults" :key="idx">
                <router-link v-if="val.type == 'doc'" :to="{ name: 'downloadDocumentationFile', query: { path: val.id, view:'true' } }">{{val.name}}</router-link> <!--documents required query in URL to route for view-->
                <router-link v-else :to="{ name: val.type == 'remote' ? 'remoteOverview' : val.type == 'device' ? 'deviceOverview' : 'downloadDocumentationFile', params: { id: val.id } }">{{val.name}}</router-link>
              </li>
            </ul>
            <div style="margin-bottom: 1rem" v-else>
              <p>No history yet.</p>
            </div>
          </b-field>
        </div>
        <div v-else class="kpt-search-results columns">
          <div class="column">
            <b-field :label="`Terminals (${terminalResults.length})`">
              <span v-if="terminalResultsLoading">Fetching...</span>
              <ul class="result-list" v-else-if="!terminalResultsLoading && terminalResults.length">
                <li v-for="(result, idx) in terminalResults" :key="idx">
                  <router-link :to="{ name: 'terminalOverview', params: { id: result.id } }" @click.native="addRecentResult({ type: 'terminal', id: result.id, name: result.name })">{{result.name}}</router-link>
                </li>
              </ul>
              <span v-else>No results were found.</span>
            </b-field>
          </div>
          <div class="column">
            <b-field :label="`Subscriptions (${subscriptionResults.length})`">
              <span v-if="subscriptionResultsLoading">Fetching...</span>
              <ul class="result-list" v-else-if="!subscriptionResultsLoading && subscriptionResults.length">
                <li v-for="(result, idx) in subscriptionResults" :key="idx">
                  <router-link :to="{ name: 'subscriptionOverview', params: { id: result.id } }" @click.native="addRecentResult({ type: 'subscription', id: result.id, name: result.name })">{{result.name}}</router-link>
                </li>
              </ul>
              <span v-else>No results were found.</span>
            </b-field>
            <b-field :label="`Documentation (${docuResults.length})`">
              <span v-if="docuResultsLoading">Fetching...</span>
              <ul class="result-list" v-else-if="!docuResultsLoading && docuResults.length">
                <li :title="result.title || result.fileName" style="width: 225px" class="truncate" v-for="(result, idx) in docuResults" :key="idx" @click="addRecentResult({ type: 'file', path: result.filePath })">
                  <router-link :to="{ name: 'downloadDocumentationFile', query: { path: result.filePath, view: isPdf(result.filePath) }}" @click.native="addRecentResult({ type: 'doc', id: result.filePath, name: result.title || result.fileName })">
                    {{result.title || result.fileName}}
                  </router-link>
                </li>
              </ul>
              <span v-else>No results were found.</span>
            </b-field>
          </div>
          <b-loading :is-full-page="false" :active.sync="isSearching" :can-cancel="false"></b-loading>
        </div>
      </template>
      <!-- eslint-disable-next-line vue/no-unused-vars -->
      <template slot-scope="props">
        <span></span>
      </template>
    </b-autocomplete>
  </div>
</template>
<script>
import { debounce } from 'lodash';

export default {
  data() {
    return {
      term: '',
      data: [],
      searchCall: null
    }
  },
  methods: {
    getSearchResults() {
      if (this.term.length <= 2) return;
      if (this.searchCall) {
        this.searchCall.cancel();
        this.searchCall = null;
      }
      // eslint-disable-next-line func-names
      this.searchCall = debounce(async function () {
        await this.$store.dispatch('runAutoSearch', { term: this.term });
        await this.$store.dispatch('addSearchHistory', { term: this.term });
      }, 500);
      // KyAnalytics client plugin, event tracker
      this.$kya.event('system', 'performTopLevelSearch', 'Search from top navigation', { searchedText: this.term });
      this.searchCall();
    },
    addRecentResult(result) {
      this.$store.dispatch('addRecentResult', result);
    },
    isPdf(absPath) {
      return !absPath ? false : absPath.includes('.pdf');
    }
  },
  computed: {
    deviceResults() {
      return this.$store.state.search.deviceAutoResults;
    },
    deviceResultsLoading() {
      return this.$store.state.search.remotesSearching;
    },
    remoteResults() {
      return this.$store.state.search.remoteAutoResults;
    },
    remoteResultsLoading() {
      return this.$store.state.search.devicesSearching;
    },
    terminalResults() {
      return this.$store.state.search.terminalAutoResults;
    },
    terminalResultsLoading() {
      return this.$store.state.search.terminalsSearching;
    },
    subscriptionResults() {
      return this.$store.state.search.subscriptionAutoResults;
    },
    subscriptionResultsLoading() {
      return this.$store.state.search.subscriptionSearching;
    },
    docuResults() {
      return this.$store.state.search.docAutoResults;
    },
    docuResultsLoading() {
      return this.$store.state.search.docsSearching;
    },
    searchHistory() {
      return this.$store.state.search.searchHistory;
    },
    recentResults() {
      return this.$store.state.search.recentResults;
    },
    resultsExist() {
      return (this.$store.state.search.remoteResults.length) || (this.$store.state.search.deviceResults.length) || (this.$store.state.search.docResults.length) || (this.$store.state.search.subscriptionREsults.length);
    },
    isSearching() {
      return this.$store.state.search.autoSearching;
    },
    favoriteRemotes() {
      return this.$store.getters.remoteFavorites;
    }
  },
  created() {
    this.$store.dispatch('fetchSearchHistory');
    this.$store.dispatch('fetchRecentResults');
    this.$root.$on('focusSearch', () => {
      if (this.$refs.searchInput) {
        this.$refs.searchInput.focus();
      }
    })
  }
}
</script>
<style lang="scss">
.kpt-search-component {
  max-width: 500px;
  min-width: 300px;

  .autocomplete {
    .dropdown-menu {
      .dropdown-content {
        max-width: 500px;
        min-width: 300px;
        overflow-x: visible;
        max-height: 400px;
        //margin-left: -100px;

        a.dropdown-item {
          display: none;
        }
        div.dropdown-item {
          white-space: inherit;
        }
      }
    }
  }

  .result-list {
    margin-top: -0.5rem;
    line-height: 1.5rem;
  }

  .kpt-search-results {
    position: relative;
  }
}
</style>
